import { Tabs as RadixTabs } from 'radix-ui';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';

import { cn } from '@/utils/cn';

export const Tabs = RadixTabs.Root;

export const TabsList = forwardRef<
  ElementRef<typeof RadixTabs.List>,
  ComponentPropsWithoutRef<typeof RadixTabs.List>
>(({ className, ...props }, ref) => (
  <RadixTabs.List
    ref={ref}
    className={cn(
      // Scrollable rather than wrapping: a record with eight tabs should not push its
      // content down a row on a laptop.
      'flex gap-1 overflow-x-auto overflow-y-hidden border-b border-border',
      className,
    )}
    {...props}
  />
));

TabsList.displayName = 'TabsList';

export const TabsTrigger = forwardRef<
  ElementRef<typeof RadixTabs.Trigger>,
  ComponentPropsWithoutRef<typeof RadixTabs.Trigger>
>(({ className, ...props }, ref) => (
  <RadixTabs.Trigger
    ref={ref}
    className={cn(
      'relative -mb-px shrink-0 border-b-2 border-transparent px-3 py-2 text-body font-medium whitespace-nowrap',
      'text-fg-secondary transition-colors hover:text-fg',
      'data-[state=active]:border-brand data-[state=active]:text-brand',
      'disabled:cursor-not-allowed disabled:opacity-50',
      className,
    )}
    {...props}
  />
));

TabsTrigger.displayName = 'TabsTrigger';

export const TabsContent = forwardRef<
  ElementRef<typeof RadixTabs.Content>,
  ComponentPropsWithoutRef<typeof RadixTabs.Content>
>(({ className, ...props }, ref) => (
  <RadixTabs.Content ref={ref} className={cn('pt-4 outline-none', className)} {...props} />
));

TabsContent.displayName = 'TabsContent';
